import { dateSort, numberSort } from 'components/LrGrid';
import { formatDollar } from 'helpers/formatters';

const dateComparator = (filterLocalDateAtMidnight: Date, cellValue?: string) => {
    if (!cellValue) return 0;

    const dateParts = cellValue.split('/');
    const day = Number(dateParts[1]);
    const month = Number(dateParts[0]) - 1;
    const year = Number(dateParts[2]);
    const cellDate = new Date(year, month, day);

    if (cellDate < filterLocalDateAtMidnight) {
        return -1;
    } else if (cellDate > filterLocalDateAtMidnight) {
        return 1;
    } else {
        return 0;
    }
};

export const columnDefs = [
    {
        headerName: 'Submission ID',
        field: 'submissionId',
        cellStyle: { textAlign: 'left' },
        comparator: numberSort,
        enableRowGroup: true,
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Policy#',
        field: 'policyNumber',
        cellStyle: { textAlign: 'left' },
        comparator: numberSort,
        enableRowGroup: true,
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Location ID',
        field: 'locationId',
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
        cellStyle: { textAlign: 'left' },
        comparator: numberSort,
    },
    {
        headerName: 'Policy Effective Date',
        field: 'policyEffectiveDate',
        comparator: dateSort,
        enableRowGroup: true,
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
        filter: 'agDateColumnFilter',
        filterParams: {
            comparator: dateComparator,
        },
    },
    {
        headerName: 'Expiration Date',
        field: 'expirationDate',
        comparator: dateSort,
        enableRowGroup: true,
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
        filter: 'agDateColumnFilter',
        filterParams: {
            comparator: dateComparator,
        },
    },
    {
        headerName: 'Endorsement Effective Date',
        field: 'endorsementEffectiveDate',
        comparator: dateSort,
        enableRowGroup: true,
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
        filter: 'agDateColumnFilter',
        filterParams: {
            comparator: dateComparator,
        },
    },
    {
        headerName: 'Created Date',
        field: 'submissionCreatedDate',
        comparator: dateSort,
        enableRowGroup: true,
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
        filter: 'agDateColumnFilter',
        filterParams: {
            comparator: dateComparator,
        },
    },
    {
        headerName: 'Quoted Date',
        field: 'quotedDate',
        comparator: dateSort,
        enableRowGroup: true,
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
        filter: 'agDateColumnFilter',
        filterParams: {
            comparator: dateComparator,
        },
    },
    {
        headerName: 'Invoice Date',
        field: 'invoicedDate',
        comparator: dateSort,
        enableRowGroup: true,
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
        filter: 'agDateColumnFilter',
        filterParams: {
            comparator: dateComparator,
        },
    },
    {
        headerName: 'Named Insured',
        field: 'namedInsured',
        enableRowGroup: true,
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
        filter: 'agTextColumnFilter',
    },
    {
        headerName: 'Status',
        enableRowGroup: true,
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
        valueGetter: (params: any) => {
            if (params.data) {
                return params.data.statusType.displayValue;
            }
            return '';
        },
    },
    {
        headerName: 'Submission State',
        field: 'statusStage',
        filter: 'agTextColumnFilter',
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
        enableRowGroup: true,
        enableValue: true,
        hide: true,
    },
    {
        headerName: 'Endorsement Type',
        field: 'endorsementType',
        enableRowGroup: true,
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
        filter: 'agTextColumnFilter',
    },
    {
        headerName: 'Location Name',
        field: 'locationName',
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
        filter: 'agTextColumnFilter',
    },
    {
        headerName: 'Location Address',
        field: 'locationAddress',
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
        filter: 'agTextColumnFilter',
    },
    {
        headerName: 'Location City',
        field: 'locationCity',
        enableRowGroup: true,
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Location State',
        field: 'locationState',
        enableRowGroup: true,
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Location Zip',
        field: 'locationZip',
        enableRowGroup: true,
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Location County',
        field: 'locationCounty',
        enableRowGroup: true,
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Location Zone',
        field: 'locationZone',
        enableRowGroup: true,
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Building Value',
        field: 'buildingValue',
        valueFormatter: ({ value }: any) => formatDollar(value, 0),
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Rental Income Value',
        field: 'biRentalIncomeValue',
        valueFormatter: ({ value }: any) => formatDollar(value, 0),
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Personal Property Value',
        field: 'personalPropertyValue',
        valueFormatter: ({ value }: any) => formatDollar(value, 0),
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Other Value',
        field: 'otherValue',
        valueFormatter: ({ value }: any) => formatDollar(value, 0),
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'TIV',
        field: 'totalInsurableValue',
        valueFormatter: ({ value }: any) => formatDollar(value, 0),
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'TIV Delta',
        field: 'tivChange',
        valueFormatter: ({ value }: any) => (value > 0 ? formatDollar(value, 0) : ''),
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Policy Fee',
        field: 'policyFee',
        valueFormatter: ({ value }: any) => formatDollar(value, 2),
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Professional Fee',
        field: 'professionalFee',
        valueFormatter: ({ value }: any) => formatDollar(value, 2),
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Inspection Fee',
        field: 'inspectionFee',
        valueFormatter: ({ value }: any) => formatDollar(value, 2),
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'SLA Tax',
        field: 'slaTax',
        valueFormatter: ({ value }: any) => formatDollar(value, 2),
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'SLA Fee',
        field: 'slaFee',
        valueFormatter: ({ value }: any) => formatDollar(value, 2),
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Home State',
        field: 'homeState',
        enableRowGroup: true,
        enableValue: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Lumen Commission %',
        field: 'lumenCommissionPercentage',
        valueFormatter: ({ value }: any) => value + '%',
        valueGetter: (value: any) => {
            if (value && value.data && value.data.lumenCommissionPercentage) {
                return value.data.lumenCommissionPercentage * 100;
            }
            return 0;
        },
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        enableValue: true,
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Lumen Commission',
        field: 'lumenCommission',
        valueFormatter: ({ value }: any) => formatDollar(value, 2),
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Broker Commission %',
        field: 'brokerCommissionPercentage',
        valueFormatter: ({ value }: any) => value + '%',
        valueGetter: (value: any) => {
            if (value && value.data && value.data.brokerCommissionPercentage) {
                return value.data.brokerCommissionPercentage * 100;
            }
            return 0;
        },
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        enableValue: true,
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Broker Commission',
        field: 'brokerCommission',
        valueFormatter: ({ value }: any) => formatDollar(value, 2),
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
    },
    {
        headerName: 'Carrier Commission',
        field: 'carrierCommission',
        valueFormatter: ({ value }: any) => formatDollar(value, 2),
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'AOP Ded',
        field: 'aopDeductible',
        enableRowGroup: true,
        enableValue: true,
        hide: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Water Dmg Ded',
        field: 'wdDeductible',
        enableRowGroup: true,
        enableValue: true,
        hide: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'WHH Ded',
        field: 'whhDeductible',
        enableRowGroup: true,
        enableValue: true,
        hide: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'NS Ded',
        field: 'nsDeductible',
        enableRowGroup: true,
        enableValue: true,
        hide: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'EQ Ded',
        field: 'earthquakeDeductible',
        enableRowGroup: true,
        enableValue: true,
        hide: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Flood Ded',
        field: 'floodDeductible',
        enableRowGroup: true,
        enableValue: true,
        hide: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'EQ Limit',
        field: 'earthquakeLimit',
        enableRowGroup: true,
        enableValue: true,
        hide: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Flood Limit',
        field: 'floodLimit',
        enableRowGroup: true,
        enableValue: true,
        hide: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Premium',
        field: 'premium',
        valueFormatter: ({ value }: any) => formatDollar(value, 2),
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Base Rate',
        field: 'baseRate',
        valueFormatter: ({ value }: any) => formatDollar(value, 2),
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Credit/Debit',
        field: 'credit',
        valueFormatter: ({ value }: any) => value + '%',
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Final Rate',
        field: 'finalRate',
        valueFormatter: ({ value }: any) => formatDollar(value, 3),
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Square Footage',
        field: 'totalSquareFootage',
        enableValue: true,
        aggFunc: 'uniqueValues',
        hide: true,
        allowedAggFuncs: ['uniqueValues'],
        cellStyle: { textAlign: 'left' },
        comparator: numberSort,
    },
    {
        headerName: 'Buildings',
        field: 'numberOfBuildings',
        enableValue: true,
        aggFunc: 'uniqueValues',
        hide: true,
        allowedAggFuncs: ['uniqueValues'],
        cellStyle: { textAlign: 'left' },
        comparator: numberSort,
    },
    {
        headerName: 'Stories',
        field: 'numberOfStories',
        enableValue: true,
        aggFunc: 'uniqueValues',
        hide: true,
        allowedAggFuncs: ['uniqueValues'],
        cellStyle: { textAlign: 'left' },
        comparator: numberSort,
    },
    {
        headerName: 'Units',
        field: 'numberOfUnits',
        enableValue: true,
        aggFunc: 'uniqueValues',
        hide: true,
        allowedAggFuncs: ['uniqueValues'],
        cellStyle: { textAlign: 'left' },
        comparator: numberSort,
    },
    {
        headerName: 'Pools',
        field: 'numberOfPools',
        enableValue: true,
        aggFunc: 'uniqueValues',
        hide: true,
        allowedAggFuncs: ['uniqueValues'],
        cellStyle: { textAlign: 'left' },
        comparator: numberSort,
    },
    {
        headerName: 'Years Owned',
        field: 'numberOfOwnershipYears',
        enableValue: true,
        aggFunc: 'uniqueValues',
        hide: true,
        allowedAggFuncs: ['uniqueValues'],
        cellStyle: { textAlign: 'left' },
        comparator: numberSort,
    },
    {
        headerName: 'Year Built',
        field: 'constructionYear',
        enableValue: true,
        aggFunc: 'uniqueValues',
        hide: true,
        allowedAggFuncs: ['uniqueValues'],
        cellStyle: { textAlign: 'left' },
        comparator: numberSort,
    },
    {
        headerName: 'Roof Replaced',
        field: 'lastYearOfRoofReplacement',
        enableValue: true,
        aggFunc: 'uniqueValues',
        hide: true,
        allowedAggFuncs: ['uniqueValues'],
        cellStyle: { textAlign: 'left' },
        comparator: numberSort,
    },
    {
        headerName: 'Smoke Detectors',
        field: 'hasSmokeDetectorsInAllUnits',
        enableValue: true,
        aggFunc: 'uniqueValues',
        hide: true,
        allowedAggFuncs: ['uniqueValues'],
        cellStyle: { textAlign: 'left' },
        comparator: numberSort,
    },
    {
        headerName: 'Has Sprinklers',
        field: 'hasAutomaticSprinklers',
        enableValue: true,
        aggFunc: 'uniqueValues',
        hide: true,
        allowedAggFuncs: ['uniqueValues'],
        cellStyle: { textAlign: 'left' },
        comparator: numberSort,
    },
    {
        headerName: 'Occupied %',
        field: 'occupancyPercentage',
        valueFormatter: ({ value }: any) => value + '%',
        valueGetter: (value: any) => {
            if (value && value.data && value.data.brokerCommissionPercentage) {
                return value.data.brokerCommissionPercentage * 100;
            }
            return 0;
        },
        aggFunc: 'sum',
        hide: true,
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        enableValue: true,
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Section 8 %',
        field: 'section8Percentage',
        valueFormatter: ({ value }: any) => value + '%',
        valueGetter: (value: any) => {
            if (value && value.data && value.data.brokerCommissionPercentage) {
                return value.data.brokerCommissionPercentage * 100;
            }
            return 0;
        },
        aggFunc: 'sum',
        hide: true,
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        enableValue: true,
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Owner Occupied %',
        field: 'ownerOccupiedPercentage',
        valueFormatter: ({ value }: any) => value + '%',
        valueGetter: (value: any) => {
            if (value && value.data && value.data.brokerCommissionPercentage) {
                return value.data.brokerCommissionPercentage * 100;
            }
            return 0;
        },
        aggFunc: 'sum',
        hide: true,
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        enableValue: true,
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Sprinkler %',
        field: 'automaticSprinklersPercentage',
        valueFormatter: ({ value }: any) => value + '%',
        valueGetter: (value: any) => {
            if (value && value.data && value.data.brokerCommissionPercentage) {
                return value.data.brokerCommissionPercentage * 100;
            }
            return 0;
        },
        aggFunc: 'sum',
        hide: true,
        allowedAggFuncs: ['avg', 'max', 'min', 'sum'],
        enableValue: true,
        cellStyle: { textAlign: 'right' },
        comparator: numberSort,
        filter: 'agNumberColumnFilter',
    },
    {
        headerName: 'Location Class',
        field: 'locationClassType',
        enableRowGroup: true,
        enableValue: true,
        hide: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Ownership Type',
        field: 'ownershipType',
        enableRowGroup: true,
        enableValue: true,
        hide: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Construction Type',
        field: 'constructionType',
        enableRowGroup: true,
        enableValue: true,
        hide: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Wiring Type',
        field: 'wiringType',
        enableRowGroup: true,
        enableValue: true,
        hide: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Roof Type',
        field: 'roofType',
        enableRowGroup: true,
        enableValue: true,
        hide: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Roof Covering',
        field: 'roofCoveringType',
        enableRowGroup: true,
        enableValue: true,
        hide: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
    {
        headerName: 'Flood Zone',
        field: 'floodZoneType',
        enableRowGroup: true,
        enableValue: true,
        hide: true,
        aggFunc: 'uniqueValues',
        allowedAggFuncs: ['uniqueValues'],
    },
];
